import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { openableRow } from '../../lib/openableRow'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { ApiFigures, ApiHealthResponse, apiRating, dateTimeText, durationText } from './api'
import { StatusPill } from './DashboardPage'
import styles from './GodMode.module.css'
import own from './EmailsPage.module.css'
import pa from '../ProductActivityPage.module.css'

// API: how Control's API itself is doing, endpoint by endpoint (API services/api-timing.js). Each instance times its
// requests and writes them by minute and route every 30 seconds; this reads the last hour, day or week of them, grouped by
// the part of Control they serve, with the requests still running and the slowest of the window. Read only.

const WINDOWS: { hours: number; label: string }[] = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
]

const count = (n: number) => n.toLocaleString()

function Figures({ f }: { f: ApiFigures }) {
  return (
    <>
      <td>{count(f.n)}</td>
      <td>{f.failed ? <StatusPill status={apiRating(f)}>{count(f.failed)}</StatusPill> : '0'}</td>
      <td>{count(f.refused)}</td>
      <td className={own.nowrap}>{durationText(f.median_ms)}</td>
      <td className={own.nowrap}>{durationText(f.p95_ms)}</td>
      <td className={own.nowrap}>{durationText(f.max_ms)}</td>
    </>
  )
}

export function ApiPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const hours = WINDOWS.some((w) => String(w.hours) === params.get('hours')) ? Number(params.get('hours')) : 24
  const [data, setData] = useState<ApiHealthResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await apiJson<ApiHealthResponse>(user, `/api/god-mode/api-health?hours=${hours}`))
      setError(null)
    } catch (e: any) {
      setError(e.message)
    }
  }, [user, hours])

  useEffect(() => {
    load()
    // The instances write every 30 seconds: reading more often shows nothing new.
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 30000)
    return () => clearInterval(t)
  }, [load])

  const t = data?.totals
  const tile = (label: string, value: React.ReactNode, sub?: React.ReactNode) => (
    <div className={own.tile}>
      <span className={own.tileLabel}>{label}</span>
      {value}
      {sub && <span className={own.sub}>{sub}</span>}
    </div>
  )

  return (
    <>
      <PageHeader title="API" />
      <PageBody>
        <div className={styles.stack}>
          <div className={pa.tabTrack} role="tablist" aria-label="Window">
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                type="button"
                role="tab"
                aria-selected={hours === w.hours}
                className={hours === w.hours ? pa.tabOn : pa.tab}
                onClick={() => setParams(w.hours === 24 ? {} : { hours: String(w.hours) }, { replace: true })}
              >
                {w.label}
              </button>
            ))}
          </div>

          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}

          {data && t && (
            <>
              <div className={own.tiles}>
                {tile('Requests', <StatusPill status={apiRating(t)}>{count(t.n)}</StatusPill>)}
                {tile('Failed', <StatusPill status={t.failed ? apiRating(t) : t.n ? 'ok' : 'idle'}>{count(t.failed)}</StatusPill>, 'the API went wrong (5xx)')}
                {tile('Refused', <StatusPill status="idle">{count(t.refused)}</StatusPill>, 'not allowed or not found (4xx)')}
                {tile('Median', <StatusPill status="idle">{durationText(t.median_ms)}</StatusPill>)}
                {tile('95% within', <StatusPill status={apiRating(t)}>{durationText(t.p95_ms)}</StatusPill>, `slowest ${durationText(t.max_ms)}`)}
                {tile(
                  'Running now',
                  <StatusPill status={(data.in_flight.oldest_ms ?? 0) >= 10000 ? 'warn' : 'idle'}>{count(data.in_flight.now)}</StatusPill>,
                  data.in_flight.instances
                    ? `${data.in_flight.instances} instance${data.in_flight.instances === 1 ? '' : 's'}` +
                        (data.in_flight.oldest_route ? ` · oldest ${durationText(data.in_flight.oldest_ms)}, ${data.in_flight.oldest_route}` : '')
                    : 'no instance has written in the last 90 seconds'
                )}
              </div>

              <h2 className={own.heading}>Endpoints</h2>
              <div className={styles.tableCard}>
                {data.groups.length === 0 ? (
                  <div className={styles.empty}>No requests in this window.</div>
                ) : (
                  <table className={styles.table} aria-label="Endpoints">
                    <thead>
                      <tr>
                        <th>Part of Control</th>
                        <th>Requests</th>
                        <th>Failed</th>
                        <th>Refused</th>
                        <th>Median</th>
                        <th>95%</th>
                        <th>Slowest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.groups.map((g) => (
                        <React.Fragment key={g.group}>
                          <tr className={own.row} {...openableRow(() => setOpen((o) => (o === g.group ? null : g.group)))} aria-expanded={open === g.group}>
                            <td>{g.group}</td>
                            <Figures f={g} />
                          </tr>
                          {open === g.group &&
                            g.routes.map((r) => (
                              <tr key={`${r.method} ${r.route}`} className={own.open}>
                                <td className={own.sub}>
                                  {r.method} {r.route}
                                </td>
                                <Figures f={r} />
                              </tr>
                            ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <h2 className={own.heading}>Slowest requests</h2>
              <div className={styles.tableCard}>
                {data.slowest.length === 0 ? (
                  <div className={styles.empty}>None took a second or more.</div>
                ) : (
                  <table className={styles.table} aria-label="Slowest requests">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Endpoint</th>
                        <th>Client</th>
                        <th>Status</th>
                        <th>Took</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.slowest.map((s, i) => (
                        <tr key={i}>
                          <td className={own.nowrap}>{dateTimeText(s.at)}</td>
                          <td>
                            {s.method} {s.route}
                          </td>
                          <td>{s.client ? s.client.name || 'Another client' : '—'}</td>
                          <td>{s.status}</td>
                          <td className={own.nowrap}>{durationText(s.ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
          <p className={own.sub}>
            Failed is the API going wrong; refused is a request it turned down, such as no sign-in or a missing record. The
            median and 95% are read from time buckets, so they are close rather than exact. Each instance writes every 30
            seconds, and this page reads again as often.
          </p>
        </div>
      </PageBody>
    </>
  )
}
